import { httpClient } from '@/lib/api/httpClient'
import type {
  InventoryItem,
  CreateFoodItemInput,
  EditFoodItemInput,
  CreateClothingProductInput,
  EditClothingProductInput,
  FoodItem,
} from '@/shared/types'

const appendCategories = (formData: FormData, categories?: string[]) => {
  if (!categories || categories.length === 0) return
  categories.forEach((id) => formData.append('categories', id))
}

export const inventoryApi = {
  fetchInventory: async (vendorId: string): Promise<InventoryItem[]> => {
    const response = await httpClient.get(`/vendors/inventory/${vendorId}`)
    return response.data?.data?.inventory ?? []
  },

  createFoodItem: async (input: CreateFoodItemInput, imageFile: File): Promise<void> => {
    const formData = new FormData()
    formData.append('foodImages', imageFile)
    formData.append('name', input.name)
    formData.append('description', input.description)
    formData.append('price', input.price.toString())
    formData.append('isAvailable', input.isAvailable.toString())
    appendCategories(formData, input.categories)

    await httpClient.post(`/vendors/${input.vendorId}/food/items`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  updateFoodItem: async (input: EditFoodItemInput, imageFile?: File): Promise<void> => {
    const formData = new FormData()
    if (imageFile) {
      formData.append('foodImages', imageFile)
    }
    formData.append('name', input.name)
    formData.append('description', input.description)
    formData.append('price', input.price.toString())
    formData.append('isAvailable', input.isAvailable.toString())
    appendCategories(formData, input.categories)

    await httpClient.patch(`/vendors/${input.vendorId}/food/items/${input.itemId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  toggleFoodItemAvailability: async (item: FoodItem, vendorId: string): Promise<void> => {
    const formData = new FormData()
    formData.append('name', item.name)
    formData.append('description', item.description || '')
    formData.append('price', item.price.toString())
    formData.append('isAvailable', (!item.isAvailable).toString())

    await httpClient.patch(`/vendors/${vendorId}/food/items/${item.id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  deleteFoodItem: async (vendorId: string, itemId: string): Promise<void> => {
    await httpClient.delete(`/vendors/inventory/${vendorId}/${itemId}/FOOD`)
  },

  createClothingItem: async (input: CreateClothingProductInput, imageFiles: File[]): Promise<void> => {
    const formData = new FormData()
    imageFiles.forEach((file) => {
      formData.append('clothImages', file)
    })
    formData.append('name', input.name)
    formData.append('description', input.description)
    formData.append('brand', input.brand)
    formData.append('variant', JSON.stringify(input.variant))
    appendCategories(formData, input.categories)

    await httpClient.post(`/vendors/${input.vendorId}/clothing/products`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  updateClothingItem: async (input: EditClothingProductInput, imageFiles?: File[]): Promise<void> => {
    const formData = new FormData()
    if (imageFiles && imageFiles.length > 0) {
      imageFiles.forEach((file) => {
        formData.append('clothImages', file)
      })
    }
    formData.append('name', input.name)
    formData.append('description', input.description)
    formData.append('brand', input.brand)
    formData.append('variant', JSON.stringify(input.variant))
    appendCategories(formData, input.categories)

    await httpClient.patch(`/vendors/${input.vendorId}/clothing/products/${input.productId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  deleteClothingItem: async (vendorId: string, itemId: string): Promise<void> => {
    await httpClient.delete(`/vendors/inventory/${vendorId}/${itemId}/CLOTHING`)
  },
}
